import React, { useState, useEffect } from "react";
import ChatIcon from "../assets/UserIcons/NewChatIcon.svg";
import LearningIcon from "../assets/UserIcons/LearningSpace.svg";
import RepoIcon from "../assets/UserIcons/KnowledgeRepositoryIcon.svg";
import MySpaceIcon from "../assets/UserIcons/MyKnowledgeSpace.svg";

export default function FeaturesPage() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const features = [
    {
      id: 1,
      icon: "💬",
      title: "New Chat",
      desc: "Begin a fresh conversation to explore ideas, ask questions, or start a new task without affecting your previous chats.",
      bgColor: "from-blue-600/30 to-cyan-600/30",
      borderColor: "border-blue-400/50",
      glowColor: "shadow-blue-500/50",
    },
    {
      id: 2,
      icon: "📚",
      title: "Learning Space",
      desc: "Add your files to create smart summaries and enhance model understanding. The more data you train, the better the insight you receive.",
      bgColor: "from-purple-600/30 to-pink-600/30",
      borderColor: "border-purple-400/50",
      glowColor: "shadow-purple-500/50",
    },
    {
      id: 3,
      icon: "🏛️",
      title: "Knowledge Repository",
      desc: "View the complete record of documents and URLs you've uploaded for training. Track summaries generated, upload dates, and data activity in one place.",
      bgColor: "from-emerald-600/30 to-teal-600/30",
      borderColor: "border-emerald-400/50",
      glowColor: "shadow-emerald-500/50",
    },
    {
      id: 4,
      icon: "🔐",
      title: "My Knowledge Space",
      desc: "A secure space where your uploaded documents and saved URLs are stored. Access, manage, and organize all your files anytime.",
      bgColor: "from-orange-600/30 to-rose-600/30",
      borderColor: "border-orange-400/50",
      glowColor: "shadow-orange-500/50",
    },
  ];

  return (
    <>
      <style>{`
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI";
}

.features-page {
  min-height: 100vh;
  background: radial-gradient(circle at top, #4a4a4a 0%, #151515 70%);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  overflow: hidden;
  position: relative;
}

/* 🌌 MOVING BACKGROUND GLOW - ENHANCED */
.features-page::before,
.features-page::after {
  content: "";
  position: absolute;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.25;
  animation: floatBg 18s linear infinite;
}

.features-page::before {
  background: #6f8cff;
  top: -120px;
  left: -120px;
}

.features-page::after {
  background: #9b59ff;
  bottom: -120px;
  right: -120px;
  animation-delay: -9s;
}

/* NEW: Additional floating orbs for depth */
.features-page::before {
  box-shadow: 200px 200px 80px rgba(107, 114, 255, 0.4), -200px -200px 80px rgba(155, 89, 255, 0.3);
}

@keyframes floatBg {
  0% { transform: translate(0, 0); }
  50% { transform: translate(80px, 60px); }
  100% { transform: translate(0, 0); }
}

.features-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 48px;
  z-index: 2;
}

/* 🚀 FLOATING ITEM - ENHANCED WITH STAGGER */
.features-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: floatItem 6s ease-in-out infinite;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.features-item:nth-child(1) { animation-delay: 0s; }
.features-item:nth-child(2) { animation-delay: 0.8s; }
.features-item:nth-child(3) { animation-delay: 1.6s; }
.features-item:nth-child(4) { animation-delay: 2.4s; }

.features-item:hover {
  transform: scale(1.08) translateY(-20px);
}

@keyframes floatItem {
  0% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
  100% { transform: translateY(0); }
}

/* 🪐 ICON ORBIT EFFECT - TURBO CHARGED */
.features-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255,255,255,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  font-size: 42px;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.features-icon:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.15) rotate(15deg);
  box-shadow: 0 0 40px rgba(255, 255, 255, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.4);
}

.features-icon::before {
  content: "";
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  border: 2px dashed rgba(255,255,255,0.3);
  animation: spin 10s linear infinite;
  transition: all 0.4s ease;
}

.features-icon:hover::before {
  inset: -18px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.2);
  animation: spin 6s linear infinite;
}

.features-icon::after {
  content: "";
  position: absolute;
  inset: -20px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.15);
  animation: spin 15s linear infinite reverse;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.features-icon img {
  width: 32px;
  height: 32px;
  z-index: 1;
  transition: transform 0.4s ease;
}

.features-icon:hover img {
  transform: scale(1.2);
}

/* ⚡ LINE PULSE - DYNAMIC */
.features-line {
  width: 3px;
  height: 40px;
  margin: 16px 0;
  background: linear-gradient(
    to bottom,
    rgba(255,255,255,0),
    rgba(255,255,255,1),
    rgba(255,255,255,0)
  );
  animation: pulseLine 2s infinite;
  transition: all 0.4s ease;
}

.features-item:hover .features-line {
  height: 60px;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
}

@keyframes pulseLine {
  0% { opacity: 0.3; transform: scaleY(0.8); }
  50% { opacity: 1; transform: scaleY(1); }
  100% { opacity: 0.3; transform: scaleY(0.8); }
}

/* 💎 CARD – 3D WOW - ULTIMATE ENHANCEMENT */
.features-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.05) 100%);
  max-width: 320px;
  padding: 24px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-style: preserve-3d;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.features-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 50%, rgba(255, 255, 255, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.5s ease;
  border-radius: 18px;
}

.features-item:hover .features-card::before {
  opacity: 1;
}

.features-item:hover .features-card {
  transform: perspective(1000px) rotateX(8deg) rotateY(-8deg) translateY(-16px) scale(1.05);
  box-shadow: 0 40px 80px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255, 255, 255, 0.2), 0 0 30px rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.35);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.08) 100%);
}

.features-card h3 {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  transition: all 0.4s ease;
}

.features-item:hover .features-card h3 {
  color: #fff;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
  transform: translateX(4px);
}

.features-card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.75);
  transition: all 0.4s ease;
}

.features-item:hover .features-card p {
  color: rgba(255, 255, 255, 0.9);
}

/* 📱 MOBILE – softer motion */
@media (max-width: 480px) {
  .features-row {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .features-item {
    animation: none;
  }

  .features-item:hover {
    transform: scale(1.03) translateY(-8px);
  }

  .features-icon::before,
  .features-icon::after {
    animation: none;
  }

  .features-icon:hover {
    transform: scale(1.08);
  }

  .features-card:hover {
    transform: translateY(-8px) scale(1.02);
  }

  .features-line {
    height: 30px;
  }

  .features-item:hover .features-line {
    height: 40px;
  }
}
`}</style>

      <div className="features-page">
        {/* ROW 1 */}
        <div className="features-row">
          <FeatureItem
            icon="💬"
            title="New Chat"
            desc="Begin a fresh conversation to explore ideas, ask questions, or start a new task without affecting your previous chats."
          />

          <FeatureItem
            icon="📚"
            title="Learning Space"
            desc="Add your files to create smart summaries and enhance model understanding. The more data you train, the better the insight you receive."
          />
        </div>

        {/* ROW 2 */}
        <div className="features-row">
          <FeatureItem
            icon="🏛️"
            title="Knowledge Repository"
            desc="View the complete record of documents and URLs you've uploaded for training. Track summaries generated, upload dates, and data activity in one place."
          />

          <FeatureItem
            icon="🔐"
            title="My Knowledge Space"
            desc="A secure space where your uploaded documents and saved URLs are stored. Access, manage, and organize all your files anytime."
          />
        </div>
      </div>
    </>
  );
}

/* 🔹 Reusable Feature Item - ENHANCED */
function FeatureItem({ icon, title, desc }) {
  return (
    <div className="features-item">
      <div className="features-icon">{icon}</div>

      <div className="features-line" />

      <div className="features-card">
        <h3>{title}</h3>
        <p>{desc}</p>
      </div>
    </div>
  );
}
